import React, { useCallback, useState, createRef, useEffect } from "react"

import styled from "styled-components"

import { useLocalization } from "@izettle/lingo-client"
import { spacing } from "@izettle/otto"
import { Button, Text } from "@izettle/react"

import { Error } from "@/components/error"
import { ErrorsObject } from "@/components/field/field-errors"
import { PageWrapper } from "@/components/layout-helpers"
import { useSetPageTitle } from "@/components/page-title"
import { TextField } from "@/components/text-field"
import { baseUrl } from "@/constants"
import { focusFirstFieldWithError } from "@/helpers"
import { LoaContract, LoaFormSteps, FormField } from "@/models"

import { StepHeader } from "./step-header"

const Fieldset = styled.fieldset`
  border: none;
  display: flex;
  flex-direction: column;
`

const PageTitle = styled(Text.Title3)`
  margin: ${spacing.none} ${spacing.none} ${spacing.xxSmall};
`

const IntroText = styled(Text.Regular)`
  margin: ${spacing.none} ${spacing.none} ${spacing.small};
`

const NextButton = styled(Button)`
  margin-top: ${spacing.small};
`

const HelpText = styled(Text.SmallSecondary)`
  margin: ${spacing.none};
`

type AuthorizerDetailsProps = {
  contract: LoaContract
  backUrl: string
  nextUrl: string
  validSteps: LoaFormSteps
  updateContract: (field: keyof LoaContract, value: string) => void
  redirectNext: (redirectUrl: string) => void
}

const BusinessPositionHelpText: React.FC = () => {
  const { text } = useLocalization()
  return <HelpText>{text("LOA_AUTHORIZER_DETAILS_EXAMPLE_TEXT")}</HelpText>
}

export const AuthorizerDetails: React.FC<AuthorizerDetailsProps> = ({
  contract,
  backUrl,
  nextUrl,
  validSteps,
  updateContract,
  redirectNext,
}) => {
  const { text } = useLocalization()
  const { setPageTitle } = useSetPageTitle()
  const [errors, setErrors] = useState<ErrorsObject>({})
  const [formFields] = useState<FormField[]>([
    { field: "firstName", label: "LOA_FIRST_NAME", ref: createRef<HTMLInputElement>() },
    { field: "lastName", label: "LOA_LAST_NAME", ref: createRef<HTMLInputElement>() },
    {
      field: "position",
      label: "LOA_POSITION",
      ref: createRef<HTMLInputElement>(),
      help: <BusinessPositionHelpText />,
    },
  ])

  useEffect(() => {
    const titleParts = [
      text("PROGRESS_STEP_PAGE_TITLE", [String(2), String(3)]),
      text("PROOF_OF_AUTHORIZATION"),
    ]
    const pageTitle = titleParts.join(" – ")

    setPageTitle(pageTitle)
  }, [])

  const onFormSubmit = useCallback(
    (event) => {
      event.preventDefault()
      const tempErrors: ErrorsObject = {}

      if (contract.firstName.length <= 1) {
        tempErrors.firstName = ["VALIDATION_REQUIRE_FIRST_NAME"]
      }

      if (contract.lastName === "") {
        tempErrors.lastName = ["VALIDATION_REQUIRE_LAST_NAME"]
      }

      if (contract.position === "") {
        tempErrors.position = ["VALIDATION_REQUIRE_BUSINESS_POSITION"]
      }

      setErrors(tempErrors)
      if (Object.keys(tempErrors).length === 0) redirectNext(nextUrl)
    },
    [contract, nextUrl],
  )

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      focusFirstFieldWithError(errors, formFields)
    }
  }, [errors])

  if (!validSteps.employee) {
    return (
      <Error cancelUrl={`${baseUrl}/letterOfAuthorization`} errorType="loaDataLost" />
    )
  }

  return (
    <PageWrapper>
      <StepHeader backUrl={backUrl} activeStep={2} />
      <form aria-labelledby="loa_authorizer_title" onSubmit={onFormSubmit}>
        <Fieldset>
          <legend id="loa_authorizer_title">
            <PageTitle>{text("LOA_AUTHORIZER_DETAILS_TITLE")}</PageTitle>
            <IntroText>{text("LOA_AUTHORIZER_DETAILS_INTRO_TEXT")}</IntroText>
          </legend>
          {formFields.map((formField, index) => (
            <TextField
              ref={formField.ref}
              key={formField.field}
              autofocus={index === 0}
              name={formField.field}
              value={contract[formField.field]}
              label={text(formField.label)}
              errors={errors[formField.field]}
              help={formField.help}
              onChange={(value: string) => updateContract(formField.field, value)}
            />
          ))}

          <NextButton type="submit">{text("NEXT")}</NextButton>
        </Fieldset>
      </form>
    </PageWrapper>
  )
}
